import { useEffect, useMemo, useState } from 'react';
import '../App.css'
import {
  Button,
  Modal,
  Upload,
  Typography,
  Select,
  Input,
  DatePicker,
  Table,
  Space,
  Popconfirm,
  message,
  Tooltip,
  Tag,
} from 'antd';
import {
  UploadOutlined,
  FileAddOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import employeeStore from '../store/EmployeeStore';
import { useBitrix } from '../context/BitrixContext';
import certificateStore from '../store/CertificateStore';

const { Text } = Typography;
const { Option } = Select;

const FIELD_OPTIONS = ['Название', 'Сотрудник', 'Дата', 'Инфо', 'Ссылка'];
const FIELD_MAP = {
  'Название': 'name',
  'Сотрудник': 'employee',
  'Дата': 'date',
  'Инфо': 'info',
  'Ссылка': 'folder_link'
};

const API_BASE = "https://bitrixapi.tmg-din-reestr.ru";

const normalizeName = (val) =>
  String(val ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

export default function ExcelUpload() {
    const { user, auth } = useBitrix();
  const [checked, setChecked] = useState(false);

  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  const [rawHeaders, setRawHeaders] = useState([]);
  const [headerMap, setHeaderMap] = useState({});
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState({}); // { rowKey: [messages] }
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
const [isEdited, setIsEdited] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (employeeStore.employees.length === 0) {
      employeeStore.fetchEmployees();
    }
  }, []);

  const showModal = () => {
    setVisible(true);
    resetState();
  };

  const hideModal = () => {
    setVisible(false);
    resetState();
  };

  const resetState = () => {
    setFile(null);
    setRawHeaders([]);
    setHeaderMap({});
    setRows([]);
    setErrors({});
    setChecked(false); // сброс состояния проверки
  };

  // Скачивание шаблона
  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const wsData = [
      ['Название', 'Сотрудник', 'Дата', 'Инфо', 'Ссылка'],
      ['Пример сертификата', 'Иванов Иван', '2025-12-31', 'Тестовый сертификат', 'https://...'],
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, 'Шаблон');
    XLSX.writeFile(wb, 'Сертификаты_шаблон.xlsx');
  };

const handleUpload = async (uploadedFile) => {
  const reader = new FileReader();
  reader.onload = async (e) => {
    try {
      const wb = XLSX.read(e.target.result, { type: 'binary' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { header: 1 });

      const headers = (json[0] || []).slice(0, 5); // только первые 5
      const dataRows = json.slice(1).map(row => row.slice(0, 5));

      const maxCols = Math.max(...dataRows.map(r => r.length));
      const fullHeaders = [...headers];
      while (fullHeaders.length < maxCols) fullHeaders.push(`Столбец ${fullHeaders.length + 1}`);

      // Автоопределение полей
      const detected = {};
      headers.forEach((h, i) => {
        const norm = normalizeName(h);
        if (norm.includes('назв')) detected[i] = 'Название';
        else if (norm.includes('сотр')) detected[i] = 'Сотрудник';
        else if (norm.includes('дат')) detected[i] = 'Дата';
        else if (norm.includes('инфо')) detected[i] = 'Инфо';
        else if (norm.includes('ссыл')) detected[i] = 'Ссылка';
      });

      // Определяем индекс колонки сотрудников
      const employeeColIdx = Object.entries(detected).find(([idx, val]) => val === 'Сотрудник')?.[0];

      // Обработка всех строк асинхронно
      const processed = await Promise.all(dataRows.map(async (row, rowIdx) => {
        const record = { key: rowIdx };
        for (let i = 0; i < headers.length; i++) {
          let cell = row[i] ?? '';

          // Обработка даты
          if (detected[i] === 'Дата') {
            if (typeof cell === 'number') {
              const parsed = XLSX.SSF.parse_date_code(cell);
              if (parsed) {
                const { y, m, d } = parsed;
                cell = `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
              }
            } else if (typeof cell === 'string') {
              const parsed = dayjs(cell);
              if (parsed.isValid()) cell = parsed.format('YYYY-MM-DD');
            }
          }

          record[i] = cell;
        }

        // Асинхронное сопоставление сотрудника
        if (employeeColIdx !== undefined) {
          const empNameRaw = record[employeeColIdx];
          const empNorm = normalizeName(empNameRaw);
          const empObj = await employeeStore.findEmployeeIdByName(empNorm); // async
          if (empObj) record[employeeColIdx] = empObj;
        }

        return record;
      }));

      setFile(uploadedFile);
      setRawHeaders(fullHeaders);
      setHeaderMap(detected);
      setRows(processed);
      setErrors({});
    } catch (err) {
      messageApi.error('Ошибка при чтении Excel-файла');
      console.error(err);
    }
  };
  reader.readAsBinaryString(uploadedFile);
  return false;
};


  const updateCell = (rowKey, colIdx, value) => {
    setRows((prev) =>
      prev.map((row) => (row.key === rowKey ? { ...row, [colIdx]: value } : row))
    );
    setIsEdited(true); // <--- любое изменение блокирует сохранение
  };

  const deleteRow = (rowKey) => {
    setRows(prev => prev.filter(row => row.key !== rowKey));
    setErrors(prev => {
      const copy = { ...prev };
      delete copy[rowKey];
      return copy;
    });
  };

const columns = useMemo(() => {
  // Добавляем колонку статуса в начало
  const statusColumn = {
    title: 'Статус',
    dataIndex: 'status',
    key: 'status',
    width: 120,
    render: (_, row) => {
  const rowError = errors[row.key]; // объект с результатом проверки
  const rowStatus = rowError?.status; // undefined при первой загрузке

  // Если ещё нет проверки — белый фон и пустой статус
  let color = '#fff';
  let displayStatus = '';

  if (rowStatus) {
    if (rowStatus === 'fail') {
      color = 'red';       
      displayStatus = 'Ошибка';
    } else if (rowStatus === 'add') {
      color = 'green';       
      displayStatus = 'Новое';
    } else if (rowStatus === 'update') {
      color = 'blue';   
      displayStatus = 'Сопоставлено';
    }
  }

  return (
    <>
          <Tag color={color}>{displayStatus}</Tag>
</>
    
  );
}

  };

  // Остальные колонки
  const dataColumns = rawHeaders.map((header, idx) => ({
    title: (
      <Select
        value={headerMap[idx] ?? undefined}
        onChange={(val) =>
          setHeaderMap((prev) => ({ ...prev, [idx]: val }))
        }
        style={{ width: 150 }}
        placeholder="Назначить поле"
      >
        {FIELD_OPTIONS.map((opt) => (
          <Option key={opt} value={opt}>{opt}</Option>
        ))}
      </Select>
    ),
    dataIndex: idx,
    key: idx,
    width: 200,
    render: (val, row) => {
      const fieldType = headerMap[idx];
      const rowErrors = errors[row.key]?.errors || {};
      const errorMsg = (() => {
          if (fieldType === 'Сотрудник') return rowErrors['employee'];
          if (fieldType === 'Дата') return rowErrors['date'];
          if (fieldType === 'Название') return rowErrors['name'];
          return null;
        })();


const style = errorMsg ? { backgroundColor: '#ffe6e6' } : {};

if (fieldType === 'Сотрудник') {
  const rowErrors = errors[row.key]?.errors || {};
  const errorMsg = rowErrors['employee'];

  // value — это ID сотрудника, если есть
  return (
    <Tooltip title={errorMsg || ''} placement="topLeft">
      <Select
        showSearch
        className={errorMsg ? 'error-select' : ''}
        value={val ?? undefined}
        onChange={(v) => updateCell(row.key, idx, v)}
        placeholder="Выберите сотрудника"
        filterOption={(input, option) =>
          normalizeName(option?.children || '').includes(normalizeName(input))
        }
        style={{ width: '100%' }}
      >
        {employeeStore.employees.map((emp) => (
          <Option key={emp.id} value={emp.id}>
            {emp.name}
          </Option>
        ))}
      </Select>
    </Tooltip>
  );
}




      if (fieldType === 'Дата') {
        return (
          <Tooltip title={errorMsg || ''} placement="topLeft">
            <DatePicker
              format="YYYY-MM-DD"
              value={val ? dayjs(val) : null}
              onChange={(date, dateString) => updateCell(row.key, idx, dateString)}
              style={{ width: '100%', ...style }}
            />
          </Tooltip>
        );
      }

      if (fieldType === 'Название') {
        return (
          <Tooltip title={errorMsg || ''} placement="topLeft">
            <Input
              value={val}
              onChange={(e) => updateCell(row.key, idx, e.target.value)}
              style={style}
            />
          </Tooltip>
        );
      }

      return <Input value={val} onChange={(e) => updateCell(row.key, idx, e.target.value)} />;
    },
  }));

  // Колонка действий
  const actionsColumn = {
    title: 'Действия',
    key: 'actions',
    fixed: 'right',
    width: 100,
    render: (_, row) => (
      <Button onClick={() => deleteRow(row.key)} danger size="small" icon={<DeleteOutlined />} />
    ),
  };

  return [statusColumn, ...dataColumns, actionsColumn];
}, [rawHeaders, headerMap, errors, rows, employeeStore.employees]);


const handleCheck = async () => {
  if (!rows.length) return;
  setLoadingCheck(true);
  try {
    // Формируем payload по headerMap
    const payload = rows.map(row => {
      const mapped = {};
      Object.entries(headerMap).forEach(([colIdx, fieldRus]) => {
        const fieldEng = FIELD_MAP[fieldRus];
        if (fieldEng) mapped[fieldEng] = row[colIdx] ?? '';
      });
      return mapped;
    });

    const res = await fetch(`${API_BASE}/check-excel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Bitrix-Auth': auth,
        'X-Bitrix-User': user,
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    const errorMap = {};
    data.results.forEach(r => {
      errorMap[r.rowKey] = {
        status: r.status,        // failed / add / update
        messages: r.messages || [],
        errors: r.errors || {},  // { name: 'Ошибка', employee_name: 'Ошибка', date: 'Ошибка' }
        employeeId: r.employeeId || null, // <- сюда кладём ID или ФИО сотрудника
      };
    });



    setErrors(errorMap);
    messageApi.success(`Проверка завершена. Строк: ${data.results.length}`);
    setChecked(true);
    setIsEdited(false);

      } catch (err) {
        console.error(err);
        messageApi.error('Ошибка проверки');
      } finally {
        setLoadingCheck(false);
      }
      
  };


const handleUploadToServer = async () => {
  if (!rows.length) return;
  setLoadingUpload(true);
  try {
    const payload = rows.map(row => {
  const mapped = {};
  Object.entries(headerMap).forEach(([colIdx, fieldRus]) => {
    const fieldEng = FIELD_MAP[fieldRus]; // <- переводим в английский
    if (fieldEng) mapped[fieldEng] = row[colIdx] ?? '';
  });
  return mapped;
});


    const res = await fetch(`${API_BASE}/upload-excel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json',
        'X-Bitrix-Auth': auth,
        'X-Bitrix-User': user,
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    messageApi.success({ duration: 5, content: `Добавлено: ${data.added}, Обновлено: ${data.updated}, Пропущено: ${data.skipped_empty_or_no_rights}`});
    hideModal();
  } catch (err) {
    console.error(err);
    messageApi.error('Ошибка загрузки');
  } finally {
    setLoadingUpload(false);
    certificateStore.fetchCertificates()
  }
};
  return (
    <>
    {contextHolder}
      <Button onClick={showModal} icon={<FileExcelOutlined />}>
        Загрузить Excel
      </Button>
      <Modal
        title="Загрузка сертификатов из Excel"
        open={visible}
        onCancel={hideModal}
        width={1000}
        footer={[
          <Button key="download" icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
            Скачать шаблон
          </Button>,
          <Button key="check" type="default" loading={loadingCheck} onClick={handleCheck}>
            Проверить данные
          </Button>,
          <Button 
            key="upload" 
            type="primary" 
            loading={loadingUpload} 
            onClick={handleUploadToServer}
            disabled={
              !checked || 
              isEdited || // <--- новая проверка
              rows.length === 0 ||
              rows.some(row => {
                const nameCol = Object.entries(headerMap).find(([i, val]) => val === 'Название')?.[0];
                const empCol = Object.entries(headerMap).find(([i, val]) => val === 'Сотрудник')?.[0];
                return !row[nameCol] || !row[empCol];
              })
            }
          >
            Сохранить
          </Button>

        ]}
      >
        <Upload beforeUpload={handleUpload} showUploadList={false} accept=".xlsx,.xls">
          <Button icon={<UploadOutlined />}>Выберите Excel файл</Button>
        </Upload>

        {rows.length > 0 && (
          <Table
            columns={columns}
            dataSource={rows}
            rowKey="key"
            style={{ marginTop: 20 }}
            pagination={{
                defaultPageSize: 100,      // <--- дефолтное количество
                pageSizeOptions: ['10', '20', '50', '100'], // <--- варианты выбора
                showSizeChanger: true,     // <--- показать дропдаун выбора
                showQuickJumper: true,     // <--- опционально, для быстрого перехода на страницу
              }}            
  scroll={{ x: 'max-content', y: 350 }}
          />
        )}
      </Modal>
    </>
  );
}
